import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import tailwindUrl from 'tailwindcss/tailwind.css?url';
import appCssUrl from 'app.css?url';
import webComponentHostCssUrl from 'theme/web-component-host.css?url';
import { userSession } from 'state/session';
import WebComponentRoot, { IWebComponentContext } from 'components/WebComponentRoot';
import { DIALOG_HOST_TAG, LAST_RESORT_CLAIM_DELAY_MS, confirmDialogState } from 'state/confirm-dialog';
import { brandSchema } from 'data/schema';
import { webComponentAssetUrl } from './ui/stage';
import { defineElementOnce } from './bundleBootstrap';

/**
 * Last-resort host for the global confirm dialog (AVS-4404).
 *
 * `share()` blocks on a confirm dialog. A dialog only renders once some host claims it, and a
 * claimed dialog has no settle timer (`state/confirm-dialog.ts`). So when every integrator-placed
 * host is hidden — DCC hides `<e3d-sharing-dashboard>` while sharing starts — nobody renders the
 * dialog, nothing cancels it, and `share()` never resolves.
 *
 * This element is owned by the runtime, appended to `document.body`, and never hidden. It waits
 * `LAST_RESORT_CLAIM_DELAY_MS` before claiming, so every integrator-placed host keeps its
 * synchronous first refusal. It claims nothing else: toasts stay with the integrator's own hosts.
 *
 * Deliberately NOT a `wc()` element. It skips the FOUC hide/show (a hidden host could not claim),
 * skips `notifyElementLifecycle` (it is not an integrator element and must not appear in element
 * recordings), and skips favicons, fonts, and `applyUserTheme()` (pure page-level side effects an
 * invisible utility host has no business repeating).
 */
export class E3DDialogHost extends HTMLElement {
  private root: ReactDOM.Root | null = null;

  private unsubscribes: Array<() => void> = [];

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    if (this.root) {
      return;
    }

    // Zero layout footprint, yet always visible to `checkVisibility()`. `display: contents` would
    // NOT work: the spec (and happy-dom) report a `contents` element with no visible element
    // children as invisible, which is exactly the check this host exists to pass.
    this.style.position = 'fixed';
    this.style.display = 'block';
    this.style.width = '0';
    this.style.height = '0';

    this.injectStyles();
    this.applyBrandingCss();

    this.root = ReactDOM.createRoot(this.shadowRoot!);
    this.root.render(
      <WebComponentRoot
        rootElement={this.shadowRoot as ShadowRoot}
        webComponent={this as unknown as IWebComponentContext['webComponent']}
        claimsToasts={false}
        dialogClaimDelayMs={LAST_RESORT_CLAIM_DELAY_MS}
      >
        {null}
      </WebComponentRoot>
    );
  }

  disconnectedCallback() {
    queueMicrotask(() => {
      if (this.isConnected) {
        return;
      }
      this.root?.unmount();
      this.root = null;
      this.unsubscribes.forEach((unsubscribe) => unsubscribe());
      this.unsubscribes = [];

      // Unmounting above already released any claim this instance held (AVS-4351), but a dialog
      // left open still needs a claimant, and nothing else re-arms one: ensureDialogHost() is
      // otherwise only ever called from a web component's own connectedCallback. Clear the
      // instance first so ensureDialogHost() doesn't mistake this removed element for a live one.
      if (dialogHostInstance === this) {
        dialogHostInstance = null;
      }
      if (confirmDialogState.get('isOpen') && typeof document !== 'undefined' && document.body) {
        ensureDialogHost();
      }
    });
  }

  /**
   * Appends the same three stylesheets `wc()` injects. Never awaited: a slow or failing stylesheet
   * may cost this host its styling, it may never cost it its claim.
   */
  private injectStyles() {
    [tailwindUrl, appCssUrl, webComponentHostCssUrl].forEach((url) => {
      const href = webComponentAssetUrl(url);
      const existingLinks = this.shadowRoot?.querySelectorAll('link[rel="stylesheet"]') ?? [];
      const alreadyInjected = Array.from(existingLinks).some((link) => link.getAttribute('href') === href);
      if (alreadyInjected) {
        return;
      }
      const linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = href;
      this.shadowRoot?.appendChild(linkElement);
    });

    const globalStyle = document.querySelector('#e3d-theme');
    if (globalStyle) {
      this.shadowRoot?.appendChild(globalStyle.cloneNode(true));
    }
  }

  /**
   * Brand classes go on the host element, not the shadow root: the dialog's colors come from CSS
   * custom properties that inherit across the shadow boundary from the host.
   */
  private applyBrandingCss() {
    const brand = userSession().get('brand');
    if (brand) {
      this.classList.add(`brand-${brand}`);
    }
    this.classList.add('ex3d-customer');

    this.unsubscribes.push(
      userSession().on('change', (keys, newValues) => {
        if (!keys.includes('brand')) {
          return;
        }
        const newBrand = newValues[keys.indexOf('brand')];
        brandSchema.options
          .values()
          .filter((option) => option !== newBrand)
          .forEach((option) => this.classList.remove(`brand-${option}`));
        this.classList.add(`brand-${newBrand}`);
      })
    );
  }
}

let dialogHostInstance: HTMLElement | null = null;

/**
 * Appends the runtime-owned dialog host to `document.body`, once per document (AVS-4404).
 *
 * Idempotence keys on the instance still being connected, not on a module flag: a test that wipes
 * `document.body` between cases must get a fresh host, not a stale reference to a detached one.
 */
export function ensureDialogHost() {
  if (typeof document === 'undefined') {
    return;
  }

  if (!document.body) {
    document.addEventListener('DOMContentLoaded', () => ensureDialogHost(), { once: true });
    return;
  }

  if (dialogHostInstance?.isConnected) {
    return;
  }

  // `defineElementOnce` logs an error for an already-registered tag, and this runs on every mount.
  if (!customElements.get(DIALOG_HOST_TAG)) {
    defineElementOnce(DIALOG_HOST_TAG, E3DDialogHost);
  }

  const host = document.createElement(DIALOG_HOST_TAG);
  if (!(host instanceof E3DDialogHost)) {
    // The host page already registered this tag with its own class: appending it would silently
    // give up the last-resort claimant without ever rendering a dialog.
    console.error('[E3D] e3d-dialog-host is owned by another script; last-resort dialog host unavailable');
    dialogHostInstance = null;
    return;
  }

  dialogHostInstance = host;
  document.body.appendChild(dialogHostInstance);
}
